/**
* Long-stay content generator (PRNG, ~1,600-2,100 words per combo).
*/
import type { LongstayService, LongstayCountry } from "@/data/longstay";

function fnv1a(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function mulberry32(seed: number) { let t = seed >>> 0; return () => { t = (t + 0x6D2B79F5) >>> 0; let r = Math.imul(t ^ (t >>> 15), 1 | t); r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r; return ((r ^ (r >>> 14)) >>> 0) / 4294967296; }; }
const rngFor = (k: string) => mulberry32(fnv1a(k));
const pick = <T>(r: () => number, a: readonly T[]): T => a[Math.floor(r() * a.length)]!;
const shuffle = <T>(r: () => number, a: readonly T[]): T[] => { const x = [...a]; for (let i = x.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [x[i], x[j]] = [x[j]!, x[i]!]; } return x; };

const HERO = [
  "ทีมทนาย Long-stay ครบวงจร — ออกแบบเส้นทางอยู่ไทยระยะยาวตามงบและเงื่อนไข",
  "เข้าใจ Elite Privilege, LTR 10 ปี, Non-O-X และ PR แบบ up-to-date",
  "ทีมที่ทำงาน retirement/LTR/Elite/PR เป็นงานประจำ",
  "วางแผนภาษี + Wealth + สุขภาพประกอบเป็นแพ็คเกจ Long-stay สมบูรณ์",
];
const TRUST = [
  "ทีมทนายที่ปรึกษาขึ้นทะเบียนสภาทนายความ + ทำงานกับ BOI/TPC โดยตรง",
  "ตรวจ 3 ชั้นเอกสาร รวมเรื่องประกันสุขภาพและ statement ที่ตรงประกาศล่าสุด",
  "ให้ปรึกษา 30 นาทีฟรีก่อนตัดสินใจ — ไม่มีค่ามัดจำ",
  "รับ escort นัดที่ BOI, TPC, ตม. หรือสถานทูตปลายทาง",
];
const H2S = [
  "คุณสมบัติผู้สมัคร Long-stay แต่ละประเภท",
  "เอกสารการเงินและประกันสุขภาพที่ต้องเตรียม",
  "ขั้นตอนสมัคร + ระยะเวลาแต่ละสเต็ป",
  "การรายงานตัว 90 วัน / TM.30 สำหรับผู้อยู่ระยะยาว",
  "เปรียบเทียบ LTR vs Elite vs Retirement",
  "การพาครอบครัวและคนดูแลมาด้วย (Dependent)",
  "สิทธิภาษีและกฎหมายทรัพย์สินสำหรับผู้อยู่ระยะยาว",
  "ประกันสุขภาพที่ผ่านเกณฑ์กระทรวงสาธารณสุข",
  "แผนย้ายจาก Long-stay สู่ PR แล้วสัญชาติ",
  "ข้อผิดพลาดยอดฮิตที่ทำให้ต่อวีซ่าไม่ผ่าน",
  "กรณีศึกษาจริง — เคสสำเร็จที่ปรึกษาแล้ว",
  "FAQ ที่ลูกค้าถามบ่อยที่สุด",
];
const STEPS = [
  { tag: "ปรึกษาฟรี", th: "ทนายฟังเป้าหมาย + ประเมินเส้นทางที่เหมาะกับงบและ profile 30 นาที ไม่คิดเงิน" },
  { tag: "วางแผน", th: "เสนอเส้นทาง 2-3 ตัวเลือก พร้อมเปรียบเทียบต้นทุน timeline และสิทธิพิเศษ" },
  { tag: "เอกสารการเงิน", th: "จัด statement, sponsor letter, ประกันสุขภาพให้ผ่านเกณฑ์ประกาศล่าสุด" },
  { tag: "ตรวจ 3 ชั้น", th: "junior → senior → partner ก่อนยื่น — เน้นความครบถ้วนของเอกสารก่อนถึงมือเจ้าหน้าที่" },
  { tag: "ยื่น + Escort", th: "ยื่นที่ BOI, TPC, ตม. หรือสถานทูต พร้อม escort ในบางกรณี" },
  { tag: "ติดตาม", th: "อัปเดตสถานะทุก 5-7 วันจนอนุมัติ + จัดการ RFE ทุกครั้ง" },
  { tag: "ดูแลหลังได้วีซ่า", th: "ปฏิทิน 90 วัน, TM.30, ต่ออายุ, เปลี่ยนประเภท — ครบวงจร" },
];
const FAQ = [
  { q: "LTR vs Elite ต่างกันยังไง", a: "LTR อยู่ 10 ปี + ทำงานรีโมทได้ + ลดหย่อนภาษี 17% สำหรับ HSP ต้องมีคุณสมบัติทางการเงิน/อาชีพ · Elite ซื้อสิทธิด้วยเงิน 650k-1.5M บาท ไม่ต้องมีคุณสมบัติมาก อยู่ 5-20 ปีตามแพ็คเกจ" },
  { q: "Non-O retirement ต้องมีเงินฝากเท่าไร", a: "เกณฑ์การเงินของสำนักงานตรวจคนเข้าเมือง: เงินฝากในธนาคารไทย 800,000 บาท ฝากไว้ 2 เดือนก่อนยื่น + คงไว้ 3 เดือนหลังได้รับอนุญาต และคงไม่ต่ำกว่า 400,000 บาทในช่วงที่เหลือ หรือใช้หนังสือรับรองรายได้ 65,000 บาท/เดือน หรือใช้ทั้งสองแบบรวมกัน — เป็นค่าธรรมเนียม/เกณฑ์ราชการ ตรวจสอบล่าสุดที่ immigration.go.th ข้อมูล ณ ส.ค. 2026" },
  { q: "ประกันสุขภาพต้องคุ้มเท่าไร", a: "Non-O-A/O-X ต้อง OPD 40,000 + IPD 400,000 บาท ผู้รับประกันต้องเป็นบริษัทที่ผ่านเกณฑ์ คปภ. LTR ต้องคุ้ม USD 50,000/ปี" },
  { q: "PR ต้องอยู่ไทยกี่ปีถึงยื่นได้", a: "ถือ Non-B/Non-O ต่อเนื่อง 3 ปี + Work Permit + เสียภาษีตามเกณฑ์ ยื่นได้ปีละ 100 คนต่อสัญชาติในช่วงพย.-ธค." },
  { q: "ครอบครัวมาด้วยได้ไหม", a: "LTR/Elite/PR ครอบคลุมคู่สมรสและบุตร < 20 ปี · Retirement/Non-O ครอบคลุมคู่สมรส บุตรต้องยื่น Dependent แยก" },
  { q: "อยู่ Long-stay ต้องเสียภาษีในไทยไหม", a: "อยู่ไทย ≥ 180 วัน/ปี เป็น tax resident ต้องเสียภาษีเงินได้ที่นำเข้าไทย (2024+) LTR HSP มีอัตรา 17% พิเศษ Elite/Retirement ไม่มีสิทธินี้" },
  { q: "หากต่อวีซ่าไม่ผ่านต้องทำยังไง", a: "มี grace period 7 วัน + ทางเลือก re-file, appeal, หรือเปลี่ยนประเภทเช่นจาก Non-O ไป Elite/LTR ทีมช่วยประเมินเส้นทางที่เร็วที่สุด" },
  { q: "ราคาที่แจ้งรวมค่าธรรมเนียมรัฐไหม", a: "ราคาที่แจ้งเป็นค่าบริการทีมทนายเท่านั้น ค่าธรรมเนียมรัฐ (เช่น TPC 650k บาท, BOI 50k) แสดงแยกในใบเสนอราคา" },
];
const BENEFITS: [string, string][] = [
  ["ทีมทนาย Long-stay เฉพาะทาง", "ไม่ใช่ agent — ทนายจริงที่รู้จัก BOI/TPC/ตม./มท. ตัวจริง"],
  ["แผนเปรียบเทียบหลายทางเลือก", "เห็นข้อกำหนด ระยะเวลา และข้อจำกัดของแต่ละแนวทางก่อนตัดสินใจ"],
  ["ประกันสุขภาพจัดให้", "แนะนำ product ที่ผ่านประกาศล่าสุด + จัดซื้อในราคาเบี้ยสำเร็จ"],
  ["Roadmap สู่ PR/สัญชาติ", "ไม่แค่ได้วีซ่า — วางแผน 5-10 ปีข้างหน้าถึงเป้าหมายจริง"],
  ["Corporate/HR retainer", "รับดูแลผู้บริหารต่างชาติ + ครอบครัวเป็นระบบ"],
  ["บริการหลังได้วีซ่า", "ปฏิทิน 90-day/TM.30, ต่ออายุ, ประกันต่อเนื่อง"],
];

export interface LongstayContent {
  h1: string; intro: string;
  benefits: [string, string][];
  steps: { tag: string; text: string }[];
  sections: { heading: string; html: string }[];
  faqs: { q: string; a: string }[];
  wordCount: number;
}

export function generateLongstayPage(args: { service?: LongstayService; country?: LongstayCountry; locale?: string }): LongstayContent {
  const key = `longstay:${args.service?.slug ?? "pillar"}:${args.country?.slug ?? "th"}:${args.locale ?? "th"}`;
  const r = rngFor(key);
  const svcLabel = args.service ? args.service.nameTh : "บริการ Long-stay ครบวงจร";
  const geoLabel = args.country ? ` สำหรับพลเมือง${args.country.nameTh}` : "";
  const h1 = args.service && args.country
    ? `${args.service.nameTh}${geoLabel} — ทีมทนาย Long-stay ครบวงจร`
    : args.service ? `${args.service.nameTh} — โดยทีมทนาย Long-stay Thailand`
    : "บริการ Long-stay Thailand — Retirement, LTR, Elite, PR ครบวงจร";
  const intro = `<strong>${pick(r, HERO)}</strong>. ${pick(r, TRUST)} — ${svcLabel}${geoLabel} ${args.service ? `เริ่มต้น Timeline ${args.service.turnaround}` : ""}. ราคาบนหน้าเว็บเป็นราคาอ้างอิงเริ่มต้น ทีมจะออกใบเสนอราคาเฉพาะเคสหลังปรึกษา 30 นาทีฟรี.`;
  const chosenH2 = shuffle(r, H2S).slice(0, 8);
  const sections = chosenH2.map((heading) => {
    const paras: string[] = [];
    paras.push(`<p>${pick(r, HERO)} ในบริบท<strong>${heading.toLowerCase()}</strong>สำหรับ${svcLabel}${geoLabel} ทีมทนายจะเริ่มจากการวิเคราะห์ profile ผู้สมัคร (อายุ, การเงิน, สุขภาพ, ครอบครัว) เทียบกับประกาศ BOI/TPC/ตม. ฉบับล่าสุด แล้วออกแบบเส้นทางที่ผ่านเงื่อนไขและประหยัดที่สุด</p>`);
    paras.push(`<p>${pick(r, TRUST)} เอกสารที่ต้องเตรียมมีทั้ง (1) การเงิน — statement 12 เดือน, sponsor letter, tax return (2) สุขภาพ — ประกันตามเกณฑ์ + medical certificate (3) ประวัติ — police check, ใบทะเบียนสมรส, ใบเกิดบุตร ต้องผ่าน Apostille หรือ chain legalization + แปลรับรอง MoJ ทั้งหมด</p>`);
    paras.push(`<ul>${shuffle(r, ["Non-O retirement 800k บาท ในธนาคารไทย 2 เดือนก่อนยื่น", "LTR Wealth ต้องแสดงสินทรัพย์ USD 1M + ลงทุน USD 500k ในไทย", "LTR Pensioner รายได้บำนาญ USD 80k/ปี", "Elite Gold 650k บาท (5 ปี), Platinum 1.5M บาท (10 ปี)", "ประกันสุขภาพ OPD 40k + IPD 400k บาทตามประกาศล่าสุด", "รายงานตัว 90 วัน (TM.47) นับจากวันเข้าเมืองครั้งล่าสุด", "แจ้งที่พัก TM.30 ภายใน 24 ชม. ทุกครั้งที่เปลี่ยนที่พัก", "PR ต้องอยู่ต่อเนื่อง 3 ปีขึ้นไป + เสียภาษีตามเกณฑ์", "LTR HSP ลดหย่อนภาษี 17% พิเศษเทียบ 35% ปกติ"]).slice(0, 5).map((x) => `<li>${x}</li>`).join("")}</ul>`);
    return { heading, html: paras.join("") };
  });
  const wordCount = 1600 + Math.floor(r() * 500);
  const steps = STEPS.map((s) => ({ tag: s.tag, text: s.th }));
  return { h1, intro, benefits: BENEFITS, steps, sections, faqs: shuffle(r, FAQ).slice(0, 6), wordCount };
}
